import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
} from "@mui/material";
import PageHeader from "../../components/shared/PageHeader";
import PageCard from "../../components/shared/PageCard";
import { addButtonSx } from "../../components/shared/buttonStyles";
import { useToast } from "../../components/shared/Toast";
import { handleGetCountries, handleDeleteCountry } from "../../api/mosaic/accounts";
import { SimpleTable } from "../../components/mosaic/Home/SimpleTable";
import SimpleModal from "../../components/mosaic/Home/SimpleModal";
import ConfirmDialog from "../../components/shared/ConfirmDialog";
import { useAppSelector } from "../../store/reduxHooks";
import { EntityType, FormAction } from "../../api/mosaic/interface";
const CountriesPage: React.FC = () => {
  const user = useAppSelector((state) => state.userSlice.user);
  const { showSuccess, showError } = useToast();

  const [countries, setCountries] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDoneSaving, setIsDoneSaving] = useState(false);

  const [openSimpleModal, setOpenSimpleModal] = useState(false);
  const [modal, setModal] = useState<{ type: EntityType; kind: FormAction } | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<{ name: string; company_id?: number }>({ name: "" });

  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setIsLoadingData(true);
        const response = await handleGetCountries();
        setCountries(response.data.data);
        setIsLoadingData(false);
      } catch (err) {
        setIsLoadingData(false);
        console.error(err);
      }
    };
    if (user) {
      fetchCountries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDoneSaving, user]);

  const handleCreateClick = () => {
    setSelectedCountry({ name: "" });
    setModal({ type: EntityType.COUNTRY, kind: FormAction.CREATE });
    setOpenSimpleModal(true);
  };

  const handleUpdateClick = (rowData: { id: number; name: string; company_id?: number }) => {
    setSelectedCountry({
      name: rowData.name,
      company_id: rowData.company_id,
    });
    setModal({ type: EntityType.COUNTRY, kind: FormAction.UPDATE });
    setOpenSimpleModal(true);
  };

  const handleDeleteClick = (id: number) => {
    setDeleteId(id);
    setOpenConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      try {
        setIsLoading(true);
        await handleDeleteCountry(deleteId);
        setIsDoneSaving((prev) => !prev);
        showSuccess("Country deleted successfully");
      } catch (err) {
        showError("Error deleting country");
        console.error(err);
      } finally {
        setIsLoading(false);
        setOpenConfirmation(false);
        setDeleteId(null);
      }
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageHeader title="Countries" subtitle="Manage countries">
        <Button variant="contained" onClick={handleCreateClick} sx={addButtonSx}>
          Add new Country
        </Button>
      </PageHeader>
      <PageCard>
        {isLoadingData ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <SimpleTable
            handleUpdateClick={handleUpdateClick}
            tableData={countries}
            head={["Country Name"]}
            handleDelete={handleDeleteClick}
            searchPlaceholder="Search countries..."
          />
        )}
      </PageCard>

      {modal && (
        <SimpleModal
          open={openSimpleModal}
          setOpen={setOpenSimpleModal}
          setIsDoneSaving={setIsDoneSaving}
          rowData={selectedCountry}
          kind={modal.kind}
          type={modal.type}
        />
      )}

      <ConfirmDialog
        open={openConfirmation}
        title="Delete Country?"
        message="Are you sure you want to delete this country?"
        onCancel={() => setOpenConfirmation(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
      />
    </Box>
  );
};

export default CountriesPage;
